/**
 * Builds hamburger FAB actions from template categories.
 * Centralized logic prevents drift between ReviewDetailScreen and ItemDetailScreen.
 */

import { getIconName } from '../constants/hamburgerIcons';

export interface HamburgerAction {
  id: string;   // Stable unique key (e.g., 'cat-5', 'item-12')
  icon: string;
  label: string;
  onPress: () => void;
  subActions?: HamburgerAction[];
}

interface Category {
  id: number;
  name: string;
  icon?: string;
  displayOrder?: number;
  showInHamburger?: boolean;
  hamburgerIcon?: string;
  hamburgerOrder?: number;
  items?: Item[];
  subcategories?: Category[];
}

interface Item {
  id: number;
  name: string;
  displayOrder?: number;
  isOnDemand?: boolean;
  isDeprecated?: boolean;
  showInHamburger?: boolean;
  hamburgerIcon?: string;
  hamburgerOrder?: number;
}

/**
 * Build hamburger actions from template categories
 * @param categories Template categories (may be nested)
 * @param onItemPress Callback when an item is tapped
 * @returns Array of hamburger actions sorted by hamburgerOrder
 */
export function buildHamburgerActions(
  categories: Category[],
  onItemPress: (itemId: number, categoryName: string) => void
): HamburgerAction[] {
  const actions: HamburgerAction[] = [];
  const includedItemIds = new Set<number>();

  // Flatten nested categories
  const flatCategories = flattenCategories(categories);

  // 1. Categories with showInHamburger → expandable actions
  //    Sort by hamburgerOrder → displayOrder → name (deterministic tie-break)
  const hamburgerCategories = flatCategories
    .filter(cat => cat.showInHamburger)
    .sort((a, b) =>
      (a.hamburgerOrder || 0) - (b.hamburgerOrder || 0)
      || (a.displayOrder || 0) - (b.displayOrder || 0)
      || a.name.localeCompare(b.name)
    );

  for (const cat of hamburgerCategories) {
    // Filter eligible items (exclude on-demand, deprecated)
    // Sort by hamburgerOrder → displayOrder → name
    const categoryItems = (cat.items || [])
      .filter(item => !item.isOnDemand && !item.isDeprecated)
      .sort((a, b) =>
        (a.hamburgerOrder || 0) - (b.hamburgerOrder || 0)
        || (a.displayOrder || 0) - (b.displayOrder || 0)
        || a.name.localeCompare(b.name)
      );

    // Track these items to avoid duplicates
    categoryItems.forEach(item => includedItemIds.add(item.id));

    // Skip categories with no eligible items (hide dead expandable nodes)
    if (categoryItems.length === 0) continue;

    actions.push({
      id: `cat-${cat.id}`,
      icon: getIconName(cat.hamburgerIcon || cat.icon || 'folder'),
      label: cat.name,
      onPress: () => {},  // Expansion handled by ExpandableFAB
      subActions: categoryItems.map(item => ({
        id: `item-${item.id}`,
        icon: getIconName(item.hamburgerIcon || 'clipboard-text'),
        label: item.name,
        onPress: () => onItemPress(item.id, cat.name),
      })),
    });
  }

  // 2. Individual items with showInHamburger whose category is NOT in hamburger
  //    Skip items already included via category, skip on-demand/deprecated
  const individualItems: { item: Item; categoryName: string }[] = [];

  for (const cat of flatCategories) {
    if (cat.showInHamburger) continue; // Already handled above
    for (const item of (cat.items || [])) {
      if (
        item.showInHamburger &&
        !item.isOnDemand &&
        !item.isDeprecated &&
        !includedItemIds.has(item.id)
      ) {
        individualItems.push({ item, categoryName: cat.name });
      }
    }
  }

  // Sort by hamburgerOrder → displayOrder → name (deterministic tie-break)
  individualItems
    .sort((a, b) =>
      (a.item.hamburgerOrder || 0) - (b.item.hamburgerOrder || 0)
      || (a.item.displayOrder || 0) - (b.item.displayOrder || 0)
      || a.item.name.localeCompare(b.item.name)
    )
    .forEach(({ item, categoryName }) => {
      actions.push({
        id: `item-${item.id}`,
        icon: getIconName(item.hamburgerIcon || 'clipboard-text'),
        label: item.name,
        onPress: () => onItemPress(item.id, categoryName),
      });
    });

  return actions;
}

/**
 * Recursively flatten nested category structure
 */
function flattenCategories(categories: Category[]): Category[] {
  const result: Category[] = [];
  for (const cat of categories) {
    result.push(cat);
    if (cat.subcategories?.length) {
      result.push(...flattenCategories(cat.subcategories));
    }
  }
  return result;
}
